/**
 * URL encoding/decoding utilities for shareable chart links
 * Uses base64 encoding for compact, clean URLs
 */

/**
 * Encodes an array of chart IDs to a base64 string
 * @param chartIds - Array of numeric chart IDs
 * @returns Base64 encoded string
 */
export function encodeChartIds(chartIds: number[]): string {
  const csv = chartIds.join(',');
  return btoa(csv);
}

/**
 * Decodes a base64 string to an array of chart IDs
 * @param encoded - Base64 encoded string
 * @returns Array of numeric chart IDs
 */
export function decodeChartIds(encoded: string): number[] {
  try {
    const csv = atob(encoded);
    if (csv === '') return [];
    return csv.split(',').map(id => parseInt(id, 10));
  } catch {
    return [];
  }
}

/**
 * Encodes a timeframe string to base64
 * @param timeframe - Timeframe string (e.g., '1y', '6m', '3m', '1m', '15d', 'all')
 * @returns Base64 encoded string
 */
export function encodeTimeframe(timeframe: string): string {
  return btoa(timeframe);
}

/**
 * Decodes a base64 string to a timeframe
 * @param encoded - Base64 encoded string
 * @returns Timeframe string or null if invalid
 */
export function decodeTimeframe(encoded: string): string | null {
  try {
    const decoded = atob(encoded);
    const validTimeframes = ['1y', '6m', '3m', '1m', '15d', 'all'];
    if (validTimeframes.includes(decoded)) {
      return decoded;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Builds a shareable URL with chart IDs and timeframe
 * @param chartIds - Array of numeric chart IDs
 * @param timeframe - Timeframe string or null
 * @param basePath - Base path for the URL (default: '/analytics')
 * @returns Complete shareable URL
 */
export function buildShareUrl(
  chartIds: number[],
  timeframe: string | null,
  basePath: string = '/analytics'
): string {
  const url = new URL(window.location.origin + basePath);
  
  if (chartIds.length > 0) {
    url.searchParams.set('c', encodeChartIds(chartIds));
  } else {
    url.searchParams.set('c', '');
  }
  
  if (timeframe && timeframe !== 'all') {
    url.searchParams.set('t', encodeTimeframe(timeframe));
  }
  
  return url.toString();
}

/**
 * Parses URL parameters to extract chart IDs and timeframe
 * @param url - URL string to parse (default: current window URL)
 * @returns Object with chartIds and timeframe
 */
export function parseShareUrl(url: string = window.location.href): {
  chartIds: number[];
  timeframe: string | null;
} {
  try {
    const urlObj = new URL(url);
    const chartsParam = urlObj.searchParams.get('c');
    const timeframeParam = urlObj.searchParams.get('t');
    
    const chartIds = chartsParam !== null ? decodeChartIds(chartsParam) : [];
    const timeframe = timeframeParam ? decodeTimeframe(timeframeParam) : null;
    
    return { chartIds, timeframe };
  } catch {
    return { chartIds: [], timeframe: null };
  }
}

/**
 * Copies a URL to the clipboard
 * @param url - URL to copy
 * @returns Promise that resolves when URL is copied
 */
export async function copyToClipboard(url: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(url);
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = url;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      throw new Error('Failed to copy URL to clipboard');
    } finally {
      document.body.removeChild(textArea);
    }
  }
}
